import React, { useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { AppPage, UnderlineTabs, colors } from 'src/components/app-ui'
import { AI_TOOLS, AI_USES, CONNECT_TABS } from './data'
import useMcpConnections from './useMcpConnections'
import useStyles from './styles'

const copyText = text => {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text)
  const input = document.createElement('textarea')
  input.value = text
  document.body.appendChild(input)
  input.select()
  document.execCommand('copy')
  document.body.removeChild(input)
  return Promise.resolve()
}

/** Returns [copiedKey, copy(key, text)]; the key resets after 2s. */
const useCopied = () => {
  const [copied, setCopied] = useState(null)
  const timer = useRef()
  useEffect(() => () => clearTimeout(timer.current), [])
  const copy = (key, text) =>
    copyText(text).then(() => {
      setCopied(key)
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(null), 2000)
    })
  return [copied, copy]
}

const openSupport = () => {
  if (window.Intercom) window.Intercom('show')
  else window.open('https://intercom.help/mini-course-generator/en/', '_blank')
}

const formatActivity = value => {
  if (!value) return null
  // The API sends UTC without a zone designator.
  const utc = /(Z|[+-]\d\d:\d\d)$/.test(value) ? value : `${value}Z`
  const minutes = Math.round((Date.now() - new Date(utc)) / 60000)
  if (Number.isNaN(minutes)) return null
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

const StatusHero = ({ connectedNames, byTool, onDisconnect }) => {
  const classes = useStyles()
  const connected = connectedNames.length > 0
  const lastActivity = formatActivity(
    Object.values(byTool)
      .map(item => item.lastActivityAt)
      .filter(Boolean)
      .sort()
      .pop(),
  )

  return (
    <div
      className={classes.hero}
      style={{
        background: connected ? '#E8FAEE' : '#FFFFFF',
        border: `1px solid ${connected ? colors.successSoft : colors.border}`,
      }}
    >
      <span
        className={classes.heroIcon}
        style={{ color: connected ? colors.successDark : colors.muted }}
      >
        <Icon
          icon={connected ? 'tabler:plug-connected' : 'tabler:plug'}
          width={22}
          height={22}
        />
      </span>
      <div className={classes.heroText}>
        <div className={classes.heroTitle}>
          {connected
            ? `${connectedNames.join(' & ')} ${
                connectedNames.length > 1 ? 'are' : 'is'
              } connected`
            : 'No AI tool connected yet'}
        </div>
        <div className={classes.heroSub}>
          {connected
            ? `${
                lastActivity ? `Last request ${lastActivity}. ` : ''
              }Status updates on its own when someone signs in or disconnects.`
            : 'Follow the steps below. This updates on its own once you sign in from your AI tool.'}
        </div>
      </div>
      <div className={classes.chips}>
        {AI_TOOLS.map(tool => {
          const on = connectedNames.includes(tool.name)
          const mine = byTool[tool.id]?.connectedByMe
          return (
            <span key={tool.id} className={classes.chip}>
              <span
                className={classes.dot}
                style={{ background: on ? colors.success : colors.disabled }}
              />
              <span className={classes.chipName}>{tool.name}</span>
              <span className={classes.chipStatus}>
                {on ? 'Connected' : 'Not connected'}
              </span>
              {mine && (
                <button
                  type="button"
                  title={`Disconnect ${tool.name}`}
                  className={classes.chipRemove}
                  onClick={() => onDisconnect(tool.id)}
                >
                  <Icon icon="tabler:x" width={12} height={12} />
                </button>
              )}
            </span>
          )
        })}
      </div>
    </div>
  )
}

/** ChatGPT connects through the official app: its button and the path to it. */
const ChatgptApp = ({ tool }) => {
  const classes = useStyles()
  return (
    <div className={classes.app}>
      <div className={classes.appRow}>
        <a
          href={tool.appUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.addButton}
        >
          <Icon icon="tabler:plus" width={16} height={16} />
          Add to ChatGPT
        </a>
        <span className={classes.official}>
          <Icon icon="tabler:circle-check-filled" width={13} height={13} />
          Official app in ChatGPT
        </span>
      </div>
      <div className={classes.crumbs}>
        {tool.path.map((label, index) => (
          <React.Fragment key={label}>
            {index > 0 && (
              <span className={classes.crumbSep}>
                <Icon icon="tabler:chevron-right" width={13} height={13} />
              </span>
            )}
            <span className={classes.crumb}>{label}</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

const VideoCard = ({ tool }) => {
  const classes = useStyles()
  return (
    <button
      type="button"
      className={classes.video}
      onClick={() => tool.videoUrl && window.open(tool.videoUrl, '_blank')}
    >
      <div className={classes.thumb}>
        <div className={classes.thumbLines}>
          <div style={{ width: '40%', background: 'rgba(255,255,255,.3)' }} />
          <div style={{ width: '66%', background: 'rgba(255,255,255,.16)' }} />
        </div>
        <span className={classes.play}>
          <Icon icon="tabler:player-play-filled" width={18} height={18} />
        </span>
      </div>
      <div className={classes.videoText}>
        <div className={classes.videoTitle}>Watch how to connect</div>
        <div className={classes.videoDesc}>
          A screen recording of every step, from copying the URL to your first
          request.
        </div>
      </div>
      <span className={classes.watch}>
        <Icon icon="tabler:player-play" width={14} height={14} />
        Watch video
      </span>
    </button>
  )
}

const SetupGuide = ({ tool, connected, mcpUrl }) => {
  const classes = useStyles()
  const [copied, copy] = useCopied()
  const isCopied = copied === 'url'

  return (
    <div className={classes.guide}>
      <div className={classes.steps}>
        {tool.steps.map((step, index) => (
          <div key={step.title} className={classes.step}>
            <span className={classes.stepNumber}>{index + 1}</span>
            <div className={classes.stepBody}>
              <div>
                <div className={classes.stepTitle}>{step.title}</div>
                <div className={classes.stepDesc}>{step.description}</div>
              </div>
              {step.showUrl && (
                <div className={classes.url}>
                  <span className={classes.urlText}>
                    {mcpUrl || 'Loading…'}
                  </span>
                  <button
                    type="button"
                    className={classes.copy}
                    style={{
                      background: isCopied
                        ? colors.successSoft
                        : colors.primary,
                      color: isCopied ? colors.successDark : '#FFFFFF',
                    }}
                    disabled={!mcpUrl}
                    onClick={() => copy('url', mcpUrl)}
                  >
                    <Icon
                      icon={isCopied ? 'tabler:check' : 'tabler:copy'}
                      width={13}
                      height={13}
                    />
                    {isCopied ? 'Copied' : 'Copy link'}
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className={classes.guideFooter}>
        <span>
          {connected
            ? `${tool.name} is connected.`
            : `Waiting for ${tool.name} to sign in…`}
        </span>
        <span>
          Stuck?{' '}
          <button
            type="button"
            className={classes.support}
            onClick={openSupport}
          >
            Contact support
          </button>
        </span>
      </div>
    </div>
  )
}

const UsesGrid = () => {
  const classes = useStyles()
  const [copied, copy] = useCopied()

  return (
    <div className={classes.uses}>
      {AI_USES.map(use => (
        <div key={use.title} className={classes.use}>
          <div className={classes.useHead}>
            <span className={classes.useIcon}>
              <Icon icon={use.icon} width={18} height={18} />
            </span>
            <div>
              <div className={classes.stepTitle}>{use.title}</div>
              <div className={classes.stepDesc} style={{ lineHeight: 1.5 }}>
                {use.description}
              </div>
            </div>
          </div>
          <div className={classes.prompts}>
            {use.prompts.map(prompt => (
              <button
                key={prompt}
                type="button"
                title="Copy prompt"
                className={classes.prompt}
                onClick={() => copy(prompt, prompt)}
              >
                <span className={classes.promptIcon}>
                  <Icon
                    icon={copied === prompt ? 'tabler:check' : 'tabler:copy'}
                    width={12}
                    height={12}
                    color={copied === prompt ? colors.successDark : undefined}
                  />
                </span>
                {prompt}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Connect: hook Claude, ChatGPT or Manus up to the workspace via MCP.
 * Status comes from `GET mcp-connections` (any workspace member's connection
 * counts) and refreshes on its own.
 */
const ConnectPage = () => {
  const classes = useStyles()
  const [tab, setTab] = useState('setup')
  const [toolId, setToolId] = useState(AI_TOOLS[0].id)
  const { mcpUrl, byTool, disconnect } = useMcpConnections()
  const connectedNames = AI_TOOLS.filter(
    item => byTool[item.id]?.connected,
  ).map(item => item.name)

  const tool = AI_TOOLS.find(item => item.id === toolId)

  return (
    <AppPage
      title="Connect Claude, ChatGPT or Manus"
      subtitle="Create courses and ask about your learner data from the assistant you already use."
    >
      <div className={classes.content}>
        <UnderlineTabs tabs={CONNECT_TABS} value={tab} onChange={setTab} />

        {tab === 'setup' && (
          <div className={classes.setup}>
            <StatusHero
              connectedNames={connectedNames}
              byTool={byTool}
              onDisconnect={disconnect}
            />

            <div className={classes.picker}>
              {AI_TOOLS.map(item => {
                const active = item.id === toolId
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={classes.pickerItem}
                    style={{
                      background: active ? '#FFFFFF' : 'transparent',
                      color: active ? colors.navy : colors.muted,
                      fontWeight: active ? 600 : 500,
                      boxShadow: active
                        ? '0 1px 2px rgba(145,158,171,.4)'
                        : 'none',
                    }}
                    onClick={() => setToolId(item.id)}
                  >
                    {item.name}
                    {connectedNames.includes(item.name) && (
                      <span className={classes.pickerDot} />
                    )}
                  </button>
                )
              })}
            </div>

            {tool.appUrl ? (
              <ChatgptApp tool={tool} />
            ) : (
              <VideoCard tool={tool} />
            )}
            <SetupGuide
              tool={tool}
              connected={connectedNames.includes(tool.name)}
              mcpUrl={mcpUrl}
            />
          </div>
        )}

        {tab === 'uses' && <UsesGrid />}
      </div>
    </AppPage>
  )
}

export default ConnectPage
